import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { REQUEST_METHOD, buildCheckColumn } from '@laramie/ui';
import { APIS } from '../../../config/apis';
import { ServerRequest } from '../../../utils/apiweb';
import { getDateToString } from '../../../utils/convert';
import { GetMeses } from '../../../utils/helpers';
import { useLista } from '../../hooks/useLista';
import { Modal } from '../../common';

import './index.scss';

const formatCuit = (cuit) => `${cuit.substring(0,2)}-${cuit.substring(2,10)}-${cuit.substring(10)}`;
const describir = ({ tipo, numero }) => (tipo === 'CUIT') ? `CUIT ${formatCuit(numero)}` : `DNI ${numero}`;
const texto = (valor) => String(valor ?? '').trim();
// Sin mayúsculas, tildes ni diéresis, como `mapFormaJuridica`: ARCA no usa tildes y la georreferencia de OSM las pone
// en la calle, así que lo que difiere sólo en eso es el mismo dato (con esto la ñ también es la n)
const normalizar = (valor) => texto(valor).toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const igual = (a, b) => normalizar(a) === normalizar(b);
// En altura, piso y código postal la migración dejó «0» donde no había dato y rellenó con ceros adelante (`00002804`):
// un número se compara y se muestra sin esos ceros, así uno de sólo ceros queda vacío. Lo mismo un número de documento
// (tipo 'numero'): ARCA no trae los ceros de adelante, y hay D.N.I. guardados con uno
const sinCero = (valor) => /^\d+$/.test(texto(valor)) ? texto(valor).replace(/^0+/, '') : texto(valor);

// Mensaje al operador según el motivo que devuelve el backend (businessCode)
const mensajeErrorArca = (data) => {
    switch (data?.businessCode) {
        case 'ARCA_CUIT_INACTIVO':
            return 'La clave figura inactiva en ARCA: no se aplica ningún dato.';
        case 'ARCA_NO_DISPONIBLE':
            return 'ARCA no está disponible en este momento. Probá de nuevo más tarde.';
        default:
            return data?.message || 'No se pudo consultar ARCA.';
    }
};

const conDato = (fila) => {
    switch (fila.tipo) {
        case 'lista':
        case 'mes':
            return fila.arca > 0;
        case 'fecha':
        case 'direccion':
            return !!fila.arca;
        default:
            return texto(fila.arca).length > 0;
    }
};

// Un dato de ARCA con `motivo` se muestra con él y no se puede aplicar
const aplicable = (fila) => conDato(fila) && !fila.motivo;

const direccionDistinta = (direccion, domicilio) => !(
    igual(direccion?.calle, domicilio.calle) && igual(sinCero(direccion?.altura), sinCero(domicilio.numero)) &&
    igual(sinCero(direccion?.piso), sinCero(domicilio.piso)) && igual(direccion?.dpto, domicilio.dpto) &&
    igual(sinCero(direccion?.codigoPostal), sinCero(domicilio.codigoPostal)) &&
    direccion?.idProvincia === domicilio.idProvinciaLocal && direccion?.idLocalidad === domicilio.idLocalidadLocal
);

/**
 * Consulta ARCA con la identificación que la persona ya tiene cargada y muestra, campo por campo, el dato actual
 * y el de ARCA para que el operador elija cuáles aplicar (LAR-1948). Vienen elegidos los que cambian y se pueden
 * aplicar, salvo los que dejan trabajo a mano; esos y los iguales se eligen de a uno, y los que no se pueden aplicar,
 * no. No guarda nada: lo elegido vuelve al formulario por `onAplicar(campos, persona, iguales)`, con los elegidos que
 * ya eran iguales a lo actual en `iguales`. Si ARCA no identifica a la persona o la clave no está activa, sólo avisa.
 *
 * `filas(persona)` arma la comparación con los datos del formulario: { campo, etiqueta, actual, arca, tipo?,
 * lista?, sinDato?, motivo?, aMano? }, con tipo 'texto' (por defecto), 'numero' (sin los ceros de adelante), 'fecha',
 * 'lista', 'mes' o 'direccion' (una dirección del formulario contra un domicilio de ARCA). Con `motivo`, el dato de
 * ARCA se muestra con ese motivo y no se puede aplicar. Con `aMano`, aplicarlo deja trabajo a mano (el nombre que ARCA
 * trae entero en un solo campo): no viene elegido ni lo elige el tilde del encabezado. `verificarFilas(persona)`,
 * opcional, resuelve más motivos que hay que consultar antes de mostrar la comparación: { campo: motivo }.
 */
const ArcaActualizacion = ({ tipoPersonaEsperado, identificacion, filas, verificarFilas, onAplicar, onCerrar }) => {

    const [estado, setEstado] = useState(() => identificacion
        ? { paso: 'consultando', consulta: identificacion, persona: null, cuits: [], aviso: null, motivos: {} }
        : { paso: 'aviso', consulta: null, persona: null, cuits: [], motivos: {}, aviso: (tipoPersonaEsperado === 'FISICA')
            ? 'La persona no tiene un CUIT válido ni un DNI cargado en sus documentos: no se puede consultar ARCA.'
            : 'La persona no tiene un CUIT válido cargado en sus documentos: no se puede consultar ARCA.' });
    // null mientras el operador no cambió la elección con la que se abre la comparación
    const [elegidos, setElegidos] = useState(null);
    const [ubicaciones, setUbicaciones] = useState({});

    const [, getRowLista] = useLista({
        listas: ['Genero', 'FormaJuridica'],
        onLoaded: () => setUbicaciones(prev => ({...prev})),
        memo: {
            key: 'Genero&FormaJuridica',
            timeout: 0
        }
    });

    const avisar = (aviso) => setEstado(prev => ({...prev, paso: 'aviso', aviso}));

    const verificar = (persona, consulta) => {
        if (persona.tipoPersona !== tipoPersonaEsperado) {
            const nombre = (persona.tipoPersona === 'JURIDICA') ? persona.razonSocial : `${persona.nombre} ${persona.apellido}`.trim();
            const tipo = (persona.tipoPersona === 'JURIDICA') ? 'jurídica' : 'física';
            avisar(`El ${describir(consulta)} corresponde en ARCA a una persona ${tipo} (${nombre}): no se aplica ningún dato.`);
            return;
        }
        if (persona.estadoClave && persona.estadoClave !== 'ACTIVO') {
            avisar(`La persona figura en ARCA con estado «${persona.estadoClave}»: no se aplica ningún dato.`);
            return;
        }
        Promise.resolve(verificarFilas ? verificarFilas(persona) : {})
        .catch(() => ({}))
        .then(motivos => setEstado(prev => ({...prev, paso: 'comparacion', consulta, persona, motivos})));
    };

    const consultar = (consulta) => {
        setEstado(prev => ({...prev, paso: 'consultando', consulta}));
        ServerRequest(
            REQUEST_METHOD.POST, null, true,
            APIS.URLS.ARCA, '/consultar-persona',
            { tipo: consulta.tipo, numero: consulta.numero },
            (response) => {
                response.json().then(data => {
                    if (data.persona) {
                        verificar(data.persona, consulta);
                    }
                    else if (data.cuits && data.cuits.length > 1) {
                        setEstado(prev => ({...prev, paso: 'cuits', cuits: data.cuits}));
                    }
                    else {
                        avisar(`ARCA no encontró a la persona con ${describir(consulta)}: no se aplica ningún dato.`);
                    }
                }).catch(() => avisar('La respuesta de ARCA no se pudo leer.'));
            },
            (response) => {
                response.json()
                .then(data => avisar(mensajeErrorArca(data)))
                .catch(() => avisar('No se pudo consultar ARCA.'));
            },
            () => avisar('No se pudo consultar ARCA.')
        );
    };

    useEffect(() => {
        if (identificacion) consultar(identificacion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const comparacion = (estado.paso === 'comparacion')
        ? filas(estado.persona).map(fila => ({...fila, motivo: fila.motivo ?? estado.motivos[fila.campo]}))
        : [];

    // Nombres de provincia y localidad de las direcciones actuales, que el formulario guarda por id
    useEffect(() => {
        comparacion.filter(fila => fila.tipo === 'direccion' && fila.actual).forEach(({ actual }) => {
            [['Provincia', actual.idProvincia], ['Localidad', actual.idLocalidad]]
            .filter(([entidad, id]) => id > 0 && !(`${entidad}_${id}` in ubicaciones))
            .forEach(([entidad, id]) => {
                ServerRequest(
                    REQUEST_METHOD.GET, null, true,
                    APIS.URLS.ENTIDAD, `/${entidad}/${id}`, null,
                    (response) => {
                        response.json()
                        .then(row => setUbicaciones(prev => ({...prev, [`${entidad}_${id}`]: row?.nombre ?? ''})))
                        .catch(() => undefined);
                    },
                    () => undefined,
                    () => undefined
                );
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estado.paso]);

    // Todo lo que reemplaza aplicar el domicilio de ARCA, también las entrecalles y la referencia: en las personas
    // migradas el domicilio suele estar sólo en la referencia
    const textoDireccion = (direccion) => {
        if (!direccion) return '';
        const lugar = [ubicaciones[`Localidad_${direccion.idLocalidad}`], ubicaciones[`Provincia_${direccion.idProvincia}`]].filter(Boolean).join(', ');
        const entre = [direccion.entreCalle1, direccion.entreCalle2].map(texto).filter(Boolean).join(' y ');
        return [
            [texto(direccion.calle), sinCero(direccion.altura)].filter(Boolean).join(' '),
            sinCero(direccion.piso) && `piso ${sinCero(direccion.piso)}`,
            texto(direccion.dpto) && `dto. ${texto(direccion.dpto)}`,
            sinCero(direccion.codigoPostal) && `CP ${sinCero(direccion.codigoPostal)}`,
            lugar,
            entre && `entre ${entre}`,
            texto(direccion.referencia) && `referencia: ${texto(direccion.referencia)}`,
        ].filter(Boolean).join(', ');
    };

    const textoDomicilioArca = (domicilio) => [
        [domicilio.calle, domicilio.numero].map(texto).filter(Boolean).join(' '),
        texto(domicilio.piso) && `piso ${texto(domicilio.piso)}`,
        texto(domicilio.dpto) && `dto. ${texto(domicilio.dpto)}`,
        texto(domicilio.codigoPostal) && `CP ${texto(domicilio.codigoPostal)}`,
        [domicilio.localidad, domicilio.descripcionProvincia].map(texto).filter(Boolean).join(', '),
    ].filter(Boolean).join(', ');

    const valor = (fila, lado) => {
        const dato = fila[lado];
        switch (fila.tipo) {
            case 'fecha':
                return dato ? getDateToString(dato) : '';
            case 'lista':
                return (dato > 0) ? (getRowLista(fila.lista, parseInt(dato))?.nombre ?? '') : '';
            case 'mes':
                return (dato > 0) ? (GetMeses().find(mes => mes.key === parseInt(dato))?.name ?? '') : '';
            case 'direccion':
                return (lado === 'actual') ? textoDireccion(dato) : (dato ? textoDomicilioArca(dato) : '');
            case 'numero':
                return sinCero(dato);
            default:
                return texto(dato);
        }
    };

    const distinto = (fila) => (fila.tipo === 'direccion')
        ? direccionDistinta(fila.actual, fila.arca)
        : !igual(valor(fila, 'actual'), valor(fila, 'arca'));

    const cambia = (fila) => aplicable(fila) && distinto(fila);
    // Vienen elegidas, y las elige o deja el tilde del encabezado, las que cambian salvo las que dejan trabajo a mano
    // (`aMano`): esas y las iguales se eligen de a una
    const deEntrada = (fila) => cambia(fila) && !fila.aMano;

    // Lo que cambia va en negrita y lo igual, en gris
    const claseFila = (fila) => cambia(fila) ? 'arca-actualizacion-distinto'
        : (conDato(fila) && !distinto(fila)) ? 'arca-actualizacion-igual' : '';

    const elegidosDeEntrada = comparacion.filter(deEntrada).map(fila => fila.campo);
    const seleccion = elegidos ?? elegidosDeEntrada;
    // Lo elegido que ya es igual a lo actual: la vista no lo reescribe, sólo lo da por completado por ARCA
    const iguales = comparacion.filter(fila => seleccion.includes(fila.campo) && !distinto(fila)).map(fila => fila.campo);
    const filasTabla = comparacion.map(fila => ({ original: {...fila, id: fila.campo, selected: seleccion.includes(fila.campo)} }));
    const elegir = (items) => setElegidos(items.reduce((campos, { id, selected }) =>
        [...campos.filter(campo => campo !== id), ...(selected ? [id] : [])], seleccion));
    // La columna de selección de TableCustom: un tilde en cada fila que se puede aplicar, también la igual, y el del
    // encabezado, que elige o deja sólo las que vienen elegidas
    const columnaElegir = buildCheckColumn({ selectionAllowed: aplicable, onItemsSelected: elegir });
    const encabezadoElegir = buildCheckColumn({
        selectionAllowed: deEntrada,
        onItemsSelected: (items) => elegir(items.filter(({ id }) => elegidosDeEntrada.includes(id))),
    });

    const body = (
        <div className="arca-actualizacion">
            {estado.paso === 'consultando' && (
                <p className="arca-actualizacion-consultando">
                    <span className="spinner-border spinner-border-sm" /> Consultando ARCA con {describir(estado.consulta)}…
                </p>
            )}

            {estado.paso === 'cuits' && (
                <div className="arca-actualizacion-cuits">
                    <p>El {describir(estado.consulta)} tiene más de un CUIT en ARCA. Elegí cuál consultar:</p>
                    <div className="arca-actualizacion-cuit-list">
                        {estado.cuits.map(cuit => (
                            <button
                                key={cuit}
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => consultar({ tipo: 'CUIT', numero: cuit })}
                            >
                                {formatCuit(cuit)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {estado.paso === 'aviso' && (
                <div className="arca-actualizacion-aviso" role="alert">
                    <span className="material-symbols-outlined">warning</span>
                    <p>{estado.aviso} Podés seguir editando la persona a mano.</p>
                </div>
            )}

            {estado.paso === 'comparacion' && (<>
                <p className="arca-actualizacion-descripcion">
                    Datos de ARCA para el {describir({ tipo: 'CUIT', numero: estado.persona.cuit })}. Vienen marcados los que cambian:
                    lo que no quede marcado sigue como está, y nada se guarda hasta que guardes la persona.
                </p>
                <table className="table arca-actualizacion-tabla">
                    <thead>
                        <tr>
                            <th style={{ width: columnaElegir.width }}>{encabezadoElegir.Header({ page: filasTabla })}</th>
                            <th>Dato</th>
                            <th>Actual</th>
                            <th>ARCA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filasTabla.map(row => {
                            const fila = row.original;
                            return (
                                <tr key={fila.campo} className={claseFila(fila)}>
                                    <td>{columnaElegir.Cell({ value: fila.selected, row })}</td>
                                    <td>{fila.etiqueta}</td>
                                    <td>{valor(fila, 'actual') || '—'}</td>
                                    <td>
                                        {conDato(fila)
                                            ? valor(fila, 'arca')
                                            : <span className="arca-actualizacion-sin-dato">{fila.sinDato ?? 'Sin dato en ARCA'}</span>}
                                        {conDato(fila) && fila.motivo && <span className="arca-actualizacion-motivo">{fila.motivo}</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </>)}
        </div>
    );

    return (
        <Modal
            show
            size="lg"
            title="Actualizar desde ARCA"
            onEscape={onCerrar}
            dismissOnEscape
            body={body}
            footer={(estado.paso === 'comparacion') ? (<>
                <button className="btn btn-outline-primary" onClick={onCerrar}>Cancelar</button>
                <button
                    className="btn btn-primary"
                    onClick={() => onAplicar(seleccion, estado.persona, iguales)}
                    disabled={seleccion.length === 0}
                >
                    Aplicar
                </button>
            </>) : (
                <button className="btn back-button" onClick={onCerrar}>Volver</button>
            )}
            footerClassName={(estado.paso === 'comparacion') ? '' : 'f-align'}
        />
    );
};

ArcaActualizacion.propTypes = {
    tipoPersonaEsperado: PropTypes.oneOf(['FISICA', 'JURIDICA']).isRequired,
    identificacion: PropTypes.shape({ tipo: PropTypes.oneOf(['CUIT', 'DNI']), numero: PropTypes.string }),
    filas: PropTypes.func.isRequired,
    verificarFilas: PropTypes.func,
    onAplicar: PropTypes.func.isRequired,
    onCerrar: PropTypes.func.isRequired,
};

ArcaActualizacion.defaultProps = {
    identificacion: null,
    verificarFilas: null,
};

export default ArcaActualizacion;
